import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Skeleton } from "primereact/skeleton";
import { Dropdown } from "primereact/dropdown";
import { currencyList } from "~/utils/currency";
import { RadioButton } from "primereact/radiobutton";
import {
  formatDateToISOStartOfDay,
  removeTimezoneFromDateString,
} from "~/utils/dateUtil";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { useSelector } from "react-redux";
import { InputNumber } from "primereact/inputnumber";

const EmploymentInformationForm = ({
  employmentInformation,
  handleChange,
  isLoading = false,
  isEdit,
  resetForm,
  editForm = false,
}) => {
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: employmentInformation,
  });
  const entityData = useSelector((state) => state.globalEntities.entity_detail);
  const employmentTypeOptions = [
    "Contractor",
    "Contractor - Agency",
    "Contractor - Company",
    "Contractor - Individual",
    "Employer of Record",
    "Expat",
    "Local Employee",
    "Non-employee",
    "Short Term Assignment",
    "Short Term Assignment (Host)",
    "Supplier",
  ];
  const employmentStatusOptions = [
    "Active",
    "Discarded",
    "Dormant",
    "On Leave",
    "Retired",
    "Terminated",
    "Unpaid Leave",
  ];
  const compensationType = ["Hourly", "Salary"];
  useEffect(() => {
    if (resetForm) {
      reset(employmentInformation);
    }
  }, [resetForm]);
  return (
    <>
      <div className="formgrid grid" data-testid="general-info-form">
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label mb-2">Employee ID *</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="employee_identifier"
              control={control}
              rules={{
                required: "Employee ID is required.",
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={employmentInformation?.employee_identifier || ""}
                  onChange={(e) => {
                    handleChange("employee_identifier", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter ID"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.employee_identifier ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                />
              )}
            />
          )}
          {errors.employee_identifier && (
            <small className="p-error">
              {errors.employee_identifier.message}
            </small>
          )}
        </div>
        <div className="field col-12 md:col-6 sm:col-6">
          <label className="contact-label">Start date *</label>
          <div className="calendar-container">
            {isLoading ? (
              <Skeleton
                data-testid="skeleton-loader"
                className="p-4"
              ></Skeleton>
            ) : (
              <Controller
                name="start_date"
                control={control}
                rules={{
                  required: "Start date is required",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Calendar
                      {...field}
                      value={
                        employmentInformation?.start_date
                          ? removeTimezoneFromDateString(
                              employmentInformation.start_date,
                            )
                          : removeTimezoneFromDateString(field.value)
                      }
                      onChange={(e) => {
                        const isoDate = e.value
                          ? formatDateToISOStartOfDay(e.value)
                          : "";
                        field.onChange(isoDate);
                        handleChange("start_date", isoDate);
                        if (employmentInformation?.end_date) {
                          handleChange("end_date", null);
                        }
                      }}
                      placeholder="Select start date"
                      className="w-full calendar-field"
                      disabled={!isEdit}
                    />
                    <span
                      className="pi pi-calendar calendar-icon"
                      data-testid="calendar-icon"
                    ></span>
                    {!employmentInformation?.start_date && error && (
                      <small className="p-error" data-testid="start-date-error">
                        {error.message}
                      </small>
                    )}
                  </>
                )}
              />
            )}
          </div>
        </div>
        <div className="field col-12 md:col-6 sm:col-6">
          <label className="contact-label">End date</label>
          <div className="calendar-container">
            {isLoading ? (
              <Skeleton
                data-testid="skeleton-loader"
                className="p-4"
              ></Skeleton>
            ) : (
              <Controller
                name="end_date"
                control={control}
                render={({ field }) => (
                  <>
                    <Calendar
                      {...field}
                      value={
                        employmentInformation?.end_date
                          ? removeTimezoneFromDateString(
                              employmentInformation.end_date,
                            )
                          : removeTimezoneFromDateString(field.value)
                      }
                      onChange={(e) => {
                        const isoDate = e.value
                          ? formatDateToISOStartOfDay(e.value)
                          : "";

                        handleChange("end_date", isoDate);
                        field.onChange(isoDate);
                      }}
                      minDate={
                        employmentInformation?.start_date
                          ? new Date(
                              new Date(
                                employmentInformation?.start_date,
                              ).setDate(
                                new Date(
                                  employmentInformation?.start_date,
                                ).getDate() + 1,
                              ),
                            )
                          : null
                      }
                      disabled={!employmentInformation?.start_date || !isEdit}
                      placeholder="Select end date"
                      className="w-full calendar-field"
                    />
                    <span className="pi pi-calendar calendar-icon"></span>
                  </>
                )}
              />
            )}
          </div>
        </div>
        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label mb-2">Employment type </label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="employment_type"
              control={control}
              render={({ field }) => {
                return (
                  <Dropdown
                    {...field}
                    value={employmentInformation?.employment_type}
                    onChange={(e) => {
                      const newValue = e.value;
                      handleChange("employment_type", newValue);
                      field.onChange(newValue);
                    }}
                    filter
                    options={employmentTypeOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select type"
                    className={`w-full ${!isEdit && "dropdown-field"} ${
                      !isEdit && "disable"
                    }`}
                    itemTemplate={(option) => (
                      <div className="flex align-items-center">
                        <RadioButton
                          checked={
                            employmentInformation?.employment_type === option
                          }
                        />
                        <span className="ml-2">{option}</span>
                      </div>
                    )}
                    disabled={!isEdit}
                  />
                );
              }}
            />
          )}
          {errors.employment_type && (
            <small className="p-error">{errors.employment_type.message}</small>
          )}
        </div>
        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label mb-2">Employment Status * </label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="employment_status"
              control={control}
              rules={{
                required: "Employment Status is required.",
              }}
              render={({ field }) => {
                return (
                  <Dropdown
                    {...field}
                    value={employmentInformation?.employment_status}
                    onChange={(e) => {
                      const newValue = e.value;
                      handleChange("employment_status", newValue);
                      field.onChange(newValue);
                    }}
                    filter
                    options={employmentStatusOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select status"
                    className={`w-full ${!isEdit && "dropdown-field"} ${
                      !isEdit && "disable"
                    }`}
                    disabled={!isEdit}
                    itemTemplate={(option) => (
                      <div className="flex align-items-center">
                        <RadioButton
                          checked={
                            employmentInformation?.employment_status === option
                          }
                        />
                        <span className="ml-2">{option}</span>
                      </div>
                    )}
                  />
                );
              }}
            />
          )}
          {errors.employment_status && (
            <small className="p-error">
              {errors.employment_status.message}
            </small>
          )}
        </div>
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label">Job title or role level</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="job_title"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={employmentInformation?.job_title || ""}
                  onChange={(e) => {
                    handleChange("job_title", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter title or role"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.job_title ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="hsp-manger-name"
                />
              )}
            />
          )}
          {errors.job_title && (
            <small className="p-error">{errors.job_title.message}</small>
          )}
        </div>
        <div className="field col-12 md:col-12 sm:col-12">
          <label className="input-label">Cost center or department</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="cost_center"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={employmentInformation?.cost_center || ""}
                  onChange={(e) => {
                    handleChange("cost_center", e.target.value);
                    field.onChange(e);
                  }}
                  type="text"
                  placeholder="Enter cost center or department"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.cost_center ? "p-invalid" : ""
                  }`}
                  disabled={!isEdit}
                  data-testid="hsp-manger-name"
                />
              )}
            />
          )}
          {errors.cost_center && (
            <small className="p-error">{errors.cost_center.message}</small>
          )}
        </div>
        {editForm && (
          <div className="field col-6 md:col-6 sm:col-6">
            <label className="input-label mb-2">Work country</label>
            {isLoading ? (
              <Skeleton
                data-testid="skeleton-loader"
                className="p-4"
              ></Skeleton>
            ) : (
              <div className={`work__email`}>
                <div>
                  <ReactCountryFlag
                    countryCode={entityData?.country}
                    svg
                    className="flag-svg-icon"
                  />
                  {getName(entityData?.country)}
                </div>
                <i className="pi pi-lock ml-2"></i>
              </div>
            )}
          </div>
        )}
        <div className=" col-12 md:col-12 sm:col-12">
          <p>
            <b>Compensation</b>
          </p>
        </div>
        <div className="field col-6 md:col-6 sm:col-6">
          <label className="input-label mb-2">Compensation type</label>
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="compensation_type"
              control={control}
              render={({ field }) => {
                return (
                  <Dropdown
                    {...field}
                    value={employmentInformation?.compensation_type}
                    onChange={(e) => {
                      const newValue = e.value;
                      handleChange("compensation_type", newValue);
                      field.onChange(newValue);
                    }}
                    options={compensationType}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select compensation type"
                    className={`w-full ${!isEdit && "dropdown-field"} ${
                      !isEdit && "disable"
                    }`}
                    disabled={!isEdit}
                    itemTemplate={(option) => (
                      <div className="flex align-items-center">
                        <RadioButton
                          checked={
                            employmentInformation?.compensation_type === option
                          }
                        />
                        <span className="ml-2">{option}</span>
                      </div>
                    )}
                  />
                );
              }}
            />
          )}
          {errors.compensation_type && (
            <small className="p-error">
              {errors.compensation_type.message}
            </small>
          )}
        </div>
        <div className="mb-2 col-12 md:col-12 sm:col-12">
          <label className="input-label">Salary *</label>
        </div>
        <div className="field col-6 md:col-6 sm:col-6">
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="currency"
              control={control}
              rules={{
                required: "Salary is required.",
              }}
              render={({ field }) => {
                return (
                  <Dropdown
                    {...field}
                    value={employmentInformation?.currency}
                    onChange={(e) => {
                      const newValue = e.value;
                      handleChange("currency", newValue);
                      field.onChange(newValue);
                    }}
                    filter
                    options={currencyList}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Currency"
                    className={`w-full ${!isEdit && "dropdown-field"} ${
                      !isEdit && "disable"
                    }`}
                    disabled={!isEdit}
                    itemTemplate={(option) => (
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId={option?.value}
                          value={option?.value}
                          checked={
                            employmentInformation?.currency === option?.value
                          }
                          onChange={() => {
                            handleChange("currency", option?.value);
                            field.onChange(option?.value);
                          }}
                        />
                        <label htmlFor={option?.value} className="ml-2">
                          ({option?.value}) {option?.label}
                        </label>
                      </div>
                    )}
                    valueTemplate={(selectedOption, props) => {
                      const selected = currencyList.find(
                        (c) => c?.value === selectedOption?.value,
                      );
                      return selected ? (
                        <span>
                          ({selected?.value}) {selected?.label}
                        </span>
                      ) : (
                        <span className="p-dropdown-placeholder">
                          {props.placeholder}
                        </span>
                      );
                    }}
                  />
                );
              }}
            />
          )}

          {errors.currency && (
            <small className="p-error">{errors.currency.message}</small>
          )}
        </div>

        <div className="field col-6 md:col-6 sm:col-6">
          {isLoading ? (
            <Skeleton data-testid="skeleton-loader" className="p-4"></Skeleton>
          ) : (
            <Controller
              name="salary"
              control={control}
              rules={{
                required: "Salary is required.",
                pattern: {
                  value: /^[0-9,]+$/,
                  message: "Salary must contain only numbers.",
                },
              }}
              render={({ field }) => (
                <InputNumber
                  {...field}
                  value={
                    employmentInformation?.salary
                      ? Number(employmentInformation.salary)
                      : null
                  }
                  onChange={(e) => {
                    handleChange("salary", e.value?.toString());
                    field.onChange(e.value);
                  }}
                  mode="decimal"
                  locale="en-IN"
                  maxFractionDigits={10}
                  useGrouping={true}
                  placeholder="Salary"
                  className={`w-full imput-number-field ${errors.salary ? "p-invalid" : ""}`}
                  disabled={!isEdit}
                />
              )}
            />
          )}

          {errors.salary && (
            <small className="p-error">{errors.salary.message}</small>
          )}
        </div>
      </div>
    </>
  );
};
EmploymentInformationForm.propTypes = {
  employmentInformation: PropTypes.object,
  handleChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isEdit: PropTypes.bool,
};
export default EmploymentInformationForm;
